import { Doc } from '@dadohr/shared/domainTypes/document';
import { EnrollmentFormStatus } from '@dadohr/shared/domainTypes/enrollmentForms';
import { isFeatureEnabled } from '@dadohr/shared/domainTypes/features';
import {
  ExperienceTemplateStatus,
  IExperience,
  IExperienceTemplate,
  ITimelineItem,
  RetryAssigningSupportingActorItemArgs
} from '@dadohr/shared/domainTypes/experience';
import { ErroDataType as ErrorDataType } from '@dadohr/shared/domainTypes/instance';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { ScheduleChannelType } from '@dadohr/shared/domainTypes/schedules';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Dialog,
  DialogContent,
  Divider,
  Grow,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { compact, flatten, groupBy, uniq } from 'lodash';
import {
  Archive,
  ChevronDown,
  ClipboardEdit,
  Copy,
  Edit,
  Eye,
  PlayCircle,
  PlusCircle,
  StopCircle,
  Upload
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';
import { TemplateMetaGrid } from '../../../../01-standalone-app/features/Experience/components/TemplateMetaGrid';
import { AILifecycleAnalysis } from '../../../../components/AILifecycleAnalysis';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { DialogActionsHeader } from '../../../../components/DialogActionsHeader';
import { DialogActionsWithSlots } from '../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../components/DialogTitleWithTypography';
import { Loader } from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { TeaserBoxFreeTierForExperiences } from '../../../../components/TeaserBox';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { usePromise } from '../../../../hooks/usePromise';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useTrackDuration } from '../../../../hooks/useTrackDuration';
import { useTranslation, useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { Section, SectionTitle } from '../../../../layout/Section';
import { useBooleanQueryParam, useRoutes, useStringQueryParam } from '../../../../routes';
import { mapWithConcurrencySettled } from '../../../../services/concurrency';
import { useCurrentUser } from '../../../../services/currentUser';
import { useEnrollmentFormByTemplateOrGetEmptyDefault } from '../../../../services/experiences/enrollmentForms';
import {
  clearAssigneeBounceIssuesForItem,
  forceRunAutomationActions,
  ignoreDocusignIssue,
  ignoreInstanceErrorIssues,
  ignoreMissingDataIssue,
  ignoreSentItemIssues,
  ignoreSupportingActorIssue,
  resendExperienceItem,
  retryAssignSupportingActor,
  retrySendDocusingEnvelope,
  retrySendingConcreteMessage,
  skipActionsAndUpdate,
  useExperienceInstancesCurrentlyRelevant
} from '../../../../services/experiences/instance';
import {
  duplicateTemplate,
  useExperienceTemplateById
} from '../../../../services/experiences/template';
import {
  updateDoc,
  useMappedLoadingValue
} from '../../../../services/firebase';
import {
  hasCurrentUserExperienceLevelScopes,
  hasUserLevelScopes
} from '../../../../services/permissions';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { now } from '../../../../services/time';
import { isCustomFieldKey, toCustomFieldKey } from '@dadohr/shared/domainTypes/org';
import { standardUserFields } from '../../../../services/user';
import { retrySendBackgroundCheck } from '../../../Org/pages/Integrations/integrations/checkr/service';
import {
  retrySalesforceParticipantUpsert,
  retrySalesforceSubtaskWrite
} from '../../../Org/pages/Integrations/integrations/salesforce/service';
import { retryWorkdaySubtaskWrite } from '../../../Org/pages/Integrations/integrations/workday/service';
import { getAnchorFields, useAnchorFields } from '../../components/Anchors';
import { CopilotBadge } from '../../components/CopilotBadge';
import { CSVImportPage } from '../../components/CsvImport';
import { MessageTemplateDialog } from '../../components/Messages';
import { TemplateSearchButton } from '../../components/TemplateSearchDrawer';
import { TemplateStatusChip } from '../../components/TemplateStatusChip';
import {
  AutomationProblemsTable,
  collectAutomationErrors
} from './AutomationProblemsOld';
import { CalendarV2 } from './CalendarV2';
import {
  EnrollmentFormDialog,
  useEnrollmentDialogTabQueryParam
} from './EnrollmentForm/EnrollmentFormDialog';
import { EnrollmentFormSubmissionsBanner } from './EnrollmentForm/EnrollmentFormSubmissionsBanner';
import { EnrollmentFormUrlBanner } from './EnrollmentForm/EnrollmentFormUrlBanner';
import { ExperiencePreviewV2Button } from './ExperiencePreview';
import {
  AutomationActionErrorData,
  collectInstanceErrors,
  ExperienceProblemData,
  getChannelsForSentItem,
  InstanceProblemsTable,
  SentItemErrorData
} from './InstanceProblems';
import { IssuesModal } from './issues/IssuesModal';
import {
  countLocalMarksForInstances,
  getIssueLocalRetryKey,
  getIssueRetryCooldownMs,
  getIssueRetryTarget,
  isRetryableIssue
} from './issues/retryState';
import {
  countRecentResends,
  readLocalRetryMarks,
  recordLocalRetry
} from '../../../../services/experiences/retryCooldown';
import { getIssueAttemptMsgIds } from '../../../../services/experiences/concreteMessageAttempts';
import { ManualEnrollModal } from './ManualEnrollModal';
import {
  getIssueDetailsText,
  getIssueItemIdsForTracking,
  getIssueTitleText,
  getIssueTypeForTracking
} from './ParticipantDialog';
import { Participants } from './Participants';
import {
  AutomationTemplateReadOnlyDialog,
  TaskTemplateReadOnlyDialog,
  TaskTimeline,
  useTaskTimelineItemSelection
} from './TaskTimeline';
import { TIMELINE_ITEM_QUERY_PARAM, toTimelineItemIdParam } from './timelineItemId';

const HIDE_OLD_AUTOMATION_PROBLEMS = true;

// Same cap as bulk enrol (`ENROLL_CONCURRENCY`): bulk ignore runs one grouped
// write set per instance, this many instances at a time.
const ISSUE_ACTION_CONCURRENCY = 8;

// Issue types stored as an InstanceError row on `instance.instanceErrors[]`;
// ignoring flips `ignore=true` on that row by id and leaves the underlying
// subtask/op status untouched. For the worker-backed types (background check,
// Salesforce, LMS, UKG, Workday) the op keeps retrying after an ignore, and each further
// failed attempt re-surfaces a fresh row until the op is terminal — see
// `appendInstanceErrorDeduped`.
const INSTANCE_ERROR_ISSUE_TYPES: ReadonlySet<string> = new Set<string>([
  'ASSIGNEE_BOUNCE',
  'CC_SEND_ERROR',
  ErrorDataType.SALESFORCE_CONTACT_RESOLUTION_FAILED,
  ErrorDataType.SALESFORCE_WRITE_FAILED,
  ErrorDataType.CANNOT_SEND_BACKGROUND_CHECK,
  ErrorDataType.CANNOT_COMPLETE_LMS_MODULE,
  ErrorDataType.UKG_WRITE_FAILED,
  ErrorDataType.WORKDAY_WRITE_FAILED
]);

const ICON_STYLE = {
  size: 18,
  strokeWidth: '1.666666'
};

const BUTTON_STYLE = {
  variant: 'outlined' as const
};

const EnrollmentMenu = ({
  onManualEnrollClick,
  onEnrollmentFormClick,
  disabled
}: {
  onManualEnrollClick: () => void;
  onEnrollmentFormClick: () => void;
  disabled: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const tooltipTitle = t(
    'You must launch the experience before you can enroll participants'
  );

  const clickManualEnroll = () => {
    onManualEnrollClick();
    setOpen(false);
  };

  const clickEnrollForm = () => {
    onEnrollmentFormClick();
    setOpen(false);
  };

  return (
    <>
      <Tooltip
        disableHoverListener={!disabled}
        title={tooltipTitle}
      >
        <span>
          <ButtonGroup variant="contained" color="primary" disabled={disabled}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlusCircle {...ICON_STYLE} />}
              onClick={onManualEnrollClick}
              ref={ref}
              disabled={disabled}
            >
              {t('Enroll')}
            </Button>
            <Button
              color="primary"
              size="small"
              aria-controls={open ? 'enroll-split-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-label="enroll"
              aria-haspopup="menu"
              onClick={() => setOpen((x) => !x)}
              disabled={disabled}
            >
              <ChevronDown size={16} />
            </Button>
          </ButtonGroup>
        </span>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={ref.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList id="enroll-button-menu">
                  <Tooltip
                    disableHoverListener={!disabled}
                    title={tooltipTitle}
                  >
                    <div>
                      <MenuItem disabled={disabled} onClick={clickManualEnroll}>
                        <ListItemIcon>
                          <PlusCircle {...ICON_STYLE} />
                        </ListItemIcon>
                        <ListItemText>{t('Enroll manually')}</ListItemText>
                      </MenuItem>
                    </div>
                  </Tooltip>

                  <Tooltip
                    disableHoverListener={!disabled}
                    title={tooltipTitle}
                  >
                    <div>
                      <MenuItem disabled={disabled} onClick={clickEnrollForm}>
                        <ListItemIcon>
                          <ClipboardEdit {...ICON_STYLE} />
                        </ListItemIcon>
                        <ListItemText>{t('Enrollment form')}</ListItemText>
                      </MenuItem>
                    </div>
                  </Tooltip>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

const MoreActions = ({ tpl, onSetUndoData }: { tpl: Doc<IExperienceTemplate>, onSetUndoData: (data: IExperienceTemplate | null) => void; }) => {
  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const { goTo, ROUTES } = useRoutes();
  const { enqueueSnackbarSuccess } = useSnackbar();
  
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const [isCSVImportDialogOpen, setCSVImportDialogOpen] = useState(false);

  const canCreate = hasUserLevelScopes(currentUser, [
    'experience.template.create'
  ]);

  const canLaunch = hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, [
    'launch'
  ]);

  const isDadoUser = currentUser.email.endsWith('@dadohr.com');

  const items = compact(
    flatten([
      canLaunch &&
        tpl.data.status !== ExperienceTemplateStatus.ARCHIVED && [
          tpl.data.status === ExperienceTemplateStatus.DRAFT && (
            <MenuItem
              key="startFromDraft"
              onClick={() =>
                goTo(
                  ROUTES.experiences.details.edit.url(tpl.id, {
                    step: 'review'
                  })
                )
              }
            >
              <ListItemIcon>
                <PlayCircle {...ICON_STYLE} />
              </ListItemIcon>
              <ListItemText>{t('Start')}...</ListItemText>
            </MenuItem>
          ),
          tpl.data.status === ExperienceTemplateStatus.ACTIVE && (
            <MenuItem
              key="stop"
              onClick={() =>
                updateDoc(tpl, () => ({
                  status: ExperienceTemplateStatus.INACTIVE
                }))
              }
            >
              <ListItemIcon>
                <StopCircle {...ICON_STYLE} />
              </ListItemIcon>
              <ListItemText>{t('Stop')}</ListItemText>
            </MenuItem>
          ),
          tpl.data.status === ExperienceTemplateStatus.INACTIVE && (
            <MenuItem
              key="startFromInactive"
              onClick={() =>
                updateDoc(tpl, () => ({
                  status: ExperienceTemplateStatus.ACTIVE
                }))
              }
            >
              <ListItemIcon>
                <PlayCircle {...ICON_STYLE} />
              </ListItemIcon>
              <ListItemText>{t('Start')}</ListItemText>
            </MenuItem>
          ),
          <Divider key="divider1" light />
        ],
      canCreate && (
        <MenuItem
          key="duplicate"
          onClick={() =>
            duplicateTemplate(tpl.data, currentUser.id).then((d) => {
              goTo(ROUTES.experiences.details.general.url(d.id));
              enqueueSnackbarSuccess(
                t(`Created new draft based on '${tpl.data.title}'`)
              );
            })
          }
        >
          <ListItemIcon>
            <Copy {...ICON_STYLE} />
          </ListItemIcon>
          <ListItemText>{t('Duplicate')}</ListItemText>
        </MenuItem>
      ),
      tpl.data.status !== ExperienceTemplateStatus.ARCHIVED &&
        tpl.data.status !== ExperienceTemplateStatus.ACTIVE && (
          <MenuItem
            key="archive"
            onClick={() =>
              updateDoc(tpl, () => ({
                status: ExperienceTemplateStatus.ARCHIVED
              }))
            }
          >
            <ListItemIcon>
              <Archive {...ICON_STYLE} />
            </ListItemIcon>
            <ListItemText>{t('Archive')}</ListItemText>
          </MenuItem>
        ),
      tpl.data.status === ExperienceTemplateStatus.ARCHIVED && (
        <MenuItem
          key="unarchive"
          onClick={() =>
            updateDoc(tpl, () => ({
              // AI-touched templates go back to ROUGH_DRAFT so the human
              // re-validates before launching. Non-AI templates resume in
              // INACTIVE (existing behavior).
              status: tpl.data.aiFeatureUsed
                ? ExperienceTemplateStatus.ROUGH_DRAFT
                : ExperienceTemplateStatus.INACTIVE
            }))
          }
        >
          <ListItemIcon>
            <Archive {...ICON_STYLE} />
          </ListItemIcon>
          <ListItemText>{t('Unarchive')}</ListItemText>
        </MenuItem>
      ),
      isDadoUser && (
        <MenuItem
          key="csvImport"
          onClick={() => {
            setCSVImportDialogOpen(true);
            close();
          }}
        >
          <ListItemIcon>
            <Upload {...ICON_STYLE} />
          </ListItemIcon>
          <ListItemText>{t('Import CSV')}</ListItemText>
        </MenuItem>
      )
    ])
  );
  if (!items.length) {
    return null;
  }

  return (
    <>
      <Button
        id="experience-more-menu"
        {...BUTTON_STYLE}
        endIcon={<ChevronDown size={16} />}
        onClick={() => setOpen(true)}
        ref={buttonRef}
      >
        {t('More')}
      </Button>
      <Menu
        getContentAnchorEl={null}
        id="experience-more-menu"
        anchorEl={buttonRef.current}
        open={open}
        onClose={close}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {items}
      </Menu>

      {isCSVImportDialogOpen && (
        <Dialog
          open={isCSVImportDialogOpen}
          onClose={() => setCSVImportDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogContent>
            <CSVImportPage
              experience={tpl.data}
              onExperienceUpdate={(updated, original) => {
                updateDoc(tpl, () => updated);
                setCSVImportDialogOpen(false);
                if (original) {
                  onSetUndoData(original);
                }
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const IssueBar = ({ tpl }: { tpl: Doc<IExperienceTemplate> }) => {
  const [instances, loading, error] = useExperienceInstancesCurrentlyRelevant(
    tpl.data.orgId,
    tpl.id,
    tpl.data.expectedEndThreshold
  );

  useErrorLogger('Instance by Tpl id: ', error);

  if (!instances) {
    return null;
  }

  return <IssuesBarInner tpl={tpl} instances={instances} />;
};

const SectionAutomationProblems = ({
  tpl
}: {
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();

  const { id: currentUserId } = useCurrentUser();
  const [data] = useMappedLoadingValue(
    useExperienceInstancesCurrentlyRelevant(
      tpl.data.orgId,
      tpl.id,
      tpl.data.expectedEndThreshold
    ),
    (ds) => collectAutomationErrors(tpl, ds)
  );
  const [selected, setSelected] = useState(new Set<string>());
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);

  const selectedItems = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.filter((d) => selected.has(d.action.id));
  }, [selected, data]);

  if (!data || !data.length) {
    return null;
  }
  const title =
    data.length === 1
      ? t('1 problem with automations detected')
      : t('{{ count }} problems with automations detected', {
          count: data.length
        });

  return (
    <Section>
      <Alert
        severity="error"
        action={
          <Button variant="outlined" color="inherit" onClick={openDialog}>
            {t('Investigate...')}
          </Button>
        }
      >
        {title}
      </Alert>

      {dialogOpen && (
        <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="xl">
          <DialogTitleWithTypography>{title}</DialogTitleWithTypography>
          <DialogContent>
            <AutomationProblemsTable
              tpl={tpl}
              ds={data}
              selected={selected}
              setSelected={setSelected}
              onRetry={(d) =>
                skipActionsAndUpdate(
                  d.instance.id,
                  [
                    {
                      automationTplId: d.automationTpl.id,
                      actionTplIds: [d.actionTpl.id]
                    }
                  ],
                  currentUserId,
                  now()
                )
              }
              onIgnore={(d) =>
                forceRunAutomationActions(
                  tpl.data.orgId,
                  [
                    {
                      experienceTplId: d.tpl.id,
                      automationTplId: d.automationTpl.id,
                      actionTplId: d.actionTpl.id,
                      instanceId: d.instance.id
                    }
                  ],
                  Date.now()
                )
              }
            />
          </DialogContent>
          <DialogActionsWithSlots
            left={
              <>
                {!!selectedItems.length && (
                  <Typography variant="body1">
                    {selectedItems.length === 1
                      ? t('1 item selected')
                      : t('{{ count }} items selected', {
                          count: selectedItems.length
                        })}
                  </Typography>
                )}
              </>
            }
            right={
              <>
                {!!selectedItems.length && (
                  <>
                    <ButtonWithPromise
                      variant="contained"
                      color="primary"
                      pending={t('Ignore selected')}
                      onClick={async () => {
                        const groupedByInstance = groupBy(
                          selectedItems,
                          (d) => d.instance.id
                        );
                        await Promise.all(
                          Object.entries(groupedByInstance).map(
                            ([instanceId, items]) => {
                              const groupedByAutomationTplId = groupBy(
                                items,
                                (d) => d.automationTpl.id
                              );
                              return skipActionsAndUpdate(
                                instanceId,
                                Object.entries(groupedByAutomationTplId).map(
                                  ([automationTplId, xs]) => {
                                    return {
                                      automationTplId,
                                      actionTplIds: xs.map(
                                        (x) => x.action.templateId
                                      )
                                    };
                                  }
                                ),
                                currentUserId,
                                now()
                              );
                            }
                          )
                        );
                      }}
                    >
                      {t('Ignore selected')}
                    </ButtonWithPromise>
                    <ButtonWithPromise
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        forceRunAutomationActions(
                          tpl.data.orgId,
                          selectedItems.map((d) => ({
                            experienceTplId: d.tpl.id,
                            automationTplId: d.automationTpl.id,
                            actionTplId: d.actionTpl.id,
                            instanceId: d.instance.id
                          })),
                          Date.now()
                        )
                      }
                      pending={t('Retry selected')}
                    >
                      {t('Retry selected')}
                    </ButtonWithPromise>
                  </>
                )}
                <Button onClick={closeDialog}>{t('Close')}</Button>
              </>
            }
          />
        </Dialog>
      )}
    </Section>
  );
};

// SC-36146: shown after every successfully queued retry.
const RETRY_QUEUED_COPY = 'Retry in progress';
const BULK_RETRY_CONCURRENCY = 5;

const RetryQueuedBanner = styled(Alert)((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const IssuesBarInner = ({
  tpl,
  instances
}: {
  tpl: Doc<IExperienceTemplate>;
  instances: Doc<IExperience>[];
}) => {
  const { t } = useUnsafeTranslation();

  const { id: currentUserId, org } = useCurrentUser();

  const [selected, setSelected] = useState(new Set<string>());
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const issuesBarRedesignEnabled = isFeatureEnabled(org.features, 'ISSUES_BAR_REDESIGN');
  const [newIssuesModalOpen, setNewIssuesModalOpen] = useBooleanQueryParam('issuesOpen', false);
  const [, setParticipantDialogInstanceId] = useStringQueryParam('i', '');
  const [, setTimelineItemId] = useStringQueryParam(TIMELINE_ITEM_QUERY_PARAM, '');
  const { ROUTES } = useRoutes();
  const { trackEvent } = useProductAnalytics();

  const [data, loading, error] = usePromise(
    () => collectInstanceErrors(tpl, instances, org),
    [instances, org]
  );

  useErrorLogger('Collecting issues: ', error);

  // SC-36146: "retry queued, wait a few minutes" state. `nowMillis` ticks while
  // either issues dialog is open so cooldown chips count down and expire on
  // their own.
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const [nowMillis, setNowMillis] = useState(() => Date.now());
  const [localRetryMarks, setLocalRetryMarks] = useState(() =>
    readLocalRetryMarks(Date.now())
  );
  const issuesDialogOpen = dialogOpen || !!newIssuesModalOpen;
  useEffect(() => {
    if (!issuesDialogOpen) return;
    setNowMillis(Date.now());
    const id = setInterval(() => setNowMillis(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, [issuesDialogOpen]);
  const getRetryCooldownMs = useCallback(
    (d: ExperienceProblemData) =>
      getIssueRetryCooldownMs(d, nowMillis, localRetryMarks),
    [nowMillis, localRetryMarks]
  );
  const recentlyQueuedRetries = useMemo(
    () =>
      countRecentResends(instances, nowMillis) +
      countLocalMarksForInstances(
        localRetryMarks,
        new Set(instances.map((i) => i.id))
      ),
    [instances, nowMillis, localRetryMarks]
  );
  // Retries the BE records as resend events get their cooldown from those
  // events; everything else is remembered in this browser.
  const markRetriesQueued = (ds: ExperienceProblemData[]) => {
    const at = Date.now();
    let marks = localRetryMarks;
    ds.forEach((d) => {
      if (!getIssueRetryTarget(d)) {
        // Merge: with storage blocked, recordLocalRetry only returns this mark.
        marks = { ...marks, ...recordLocalRetry(getIssueLocalRetryKey(d), at) };
      }
    });
    setLocalRetryMarks(marks);
    setNowMillis(at);
  };

  const selectedItems = useMemo(
    () => (data ? data.filter((d) => selected.has(d.id)) : []),
    [selected, data]
  );

  if (!data || !data.length) {
    return null;
  }

  const title =
    data.length === 1
      ? t('1 issue detected')
      : t('{{ count }} issues detected', {
          count: data.length
        });

  const onRetryHandler = (d: ExperienceProblemData): Promise<any> => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_INSTANCE,
      'Instance ID': d.instance.id,
      name: 'Issue Retried',
      Explanation:
        'Admin user clicks retry button in issues list modal for just one issue',
      'Issue Details':
        getIssueDetailsText(d, org.customUserFields, t, {
          stripFormattingFragments: true
        }) || '',
      // `d` is `D` (= ExperienceProblemData & { participant: Doc<IUser> }) at
      // runtime — the table threads participant through every row — but the
      // public callback signature is the narrower ExperienceProblemData.
      'Timeline Item Title': getIssueTitleText(
        d,
        t,
        (d as { participant?: Doc<IUser> }).participant
      ),
      'Timeline Item Type': getIssueTypeForTracking(d),
      'Timeline Item ID': getIssueItemIdsForTracking(d)
    });
    return dispatchRetry(d);
  };

  /** The per-type retry call for one issue, with no tracking: `onRetryHandler`
   * fires the single-issue event before calling this, and the bulk paths fire
   * one "Multiple Issues Retried" event and then call this per item.
   * Resolves only once the retry was accepted; rejects with the reason
   * otherwise (SC-36146 — callers surface both to the admin). */
  const dispatchRetry = (d: ExperienceProblemData): Promise<any> => {
    if (d.type === 'AUTOMATION') {
      return forceRunAutomationActions(
        tpl.data.orgId,
        [
          {
            experienceTplId: d.tpl.id,
            automationTplId: d.automationTpl.id,
            actionTplId: d.actionTpl.id,
            instanceId: d.instance.id
          }
        ],
        Date.now()
      );
    }
    if (d.type === 'SENT_ITEM') {
      return retrySendingConcreteMessage({
        orgId: d.instance.data.orgId,
        experienceId: d.instance.id,
        message: d.message,
        actor: d.actor,
        channels: d.channels,
        messageIdsToIgnore: d.groupedMessageIds
      });
    }
    if (d.type === ErrorDataType.CANNOT_ASSIGN_SUPPORTING_ACTOR) {
      const args: RetryAssigningSupportingActorItemArgs = {
        expId: d.instance.id,
        tplId: d.tpl.id,
        subtaskTplId: d.subtaskTplId,
        orgId: d.tpl.data.orgId,
        instErr: {
          actor: d.actorKey,
          workEmail: d.workEmail,
          id: d.id,
          type: d.type
        }
      };
      return retryAssignSupportingActor({ errData: d, args, currentUserId });
    }

    if (d.type === ErrorDataType.CANNOT_SEND_DOCUSIGN_ENVELOPE) {
      return retrySendDocusingEnvelope({ errData: d });
    }

    // Salesforce retry: aborts the stale op + re-runs the hook with today's
    // template + today's user. Same shape as email retry — "redo it fresh,"
    // not "edit the queued attempt." Bead new-main-uzj1.
    if (d.type === ErrorDataType.SALESFORCE_CONTACT_RESOLUTION_FAILED) {
      return retrySalesforceParticipantUpsert({
        orgId: d.instance.data.orgId,
        experienceId: d.instance.id
      });
    }

    if (d.type === ErrorDataType.SALESFORCE_WRITE_FAILED) {
      return retrySalesforceSubtaskWrite({
        orgId: d.instance.data.orgId,
        experienceId: d.instance.id
      });
    }

    if (d.type === ErrorDataType.WORKDAY_WRITE_FAILED) {
      return retryWorkdaySubtaskWrite({
        orgId: d.instance.data.orgId,
        experienceId: d.instance.id
      });
    }

    if (d.type === ErrorDataType.CANNOT_SEND_BACKGROUND_CHECK) {
      // Re-queues the aborted BG-check op for this sub-task and clears the
      // instanceError server-side; the worker re-runs the invitation with the
      // current package + participant data.
      return retrySendBackgroundCheck({
        orgId: d.instance.data.orgId,
        experienceId: d.instance.id,
        subtaskTplId: d.subtaskTplId
      });
    }

    // Retry-on-bounce: derive actor + channels from the template (same
    // pattern SENT_ITEM rows use), look up the matching item on the
    // instance, then dispatch resend. The BE re-fetches the user profile
    // and clears Postmark suppression so an updated email is honored.
    // See bd new-main-1gz.
    if (d.type === 'ASSIGNEE_BOUNCE' && d.itemType === 'MESSAGE') {
      const messageTpl = d.tpl.data.messages.other.find(
        (m) => m.id === d.itemTplId
      );
      const originalMessage = d.instance.data.messages.find(
        (m) => m.scope?.msgId === d.itemTplId
      );
      if (messageTpl && originalMessage) {
        return retrySendingConcreteMessage({
          orgId: d.instance.data.orgId,
          experienceId: d.instance.id,
          message: originalMessage,
          actor: messageTpl.actor,
          channels: getChannelsForSentItem(messageTpl.channels, originalMessage)
        }).then(() =>
          // SC-33594: the resend clears the SENT_ITEM (message) twin, but the
          // ASSIGNEE_BOUNCE row is backed by an instanceError — clear that too,
          // or the "…bounced" issue survives a successful retry.
          clearAssigneeBounceIssuesForItem({
            instanceId: d.instance.id,
            itemType: d.itemType,
            itemTplId: d.itemTplId,
            email: d.email
          })
        );
      }
      return Promise.reject(
        new Error(
          'Cannot retry: original message context not found on this instance. Try resending from the timeline view.'
        )
      );
    }

    if (d.type === 'ASSIGNEE_BOUNCE' && d.itemType === 'TASK') {
      // BE quirk: for TASK bounces the field `itemTplId` actually holds a
      // concrete task INSTANCE id, not a template id. The Postmark scope for
      // task-start emails is populated from `task.id` (see new-main
      // functions/schedules/src/executeScheduleBatch.ts:614:
      // `taskIds = c.data.map(x => x.task.id)`), and trackEmail copies
      // `scope.taskIds[0]` into the instanceError's `itemTplId` field. So
      // look up the task on the instance by `id`, then derive the template.
      const originalTask = d.instance.data.tasks.find(
        (t) => t.id === d.itemTplId
      );
      const taskTpl = originalTask
        ? d.tpl.data.tasks.find((t) => t.id === originalTask.templateId)
        : undefined;
      if (taskTpl && originalTask) {
        // Bounce is always email; only retry the email channel(s). Other
        // channels (slack/teams/sms) on the task template were not part
        // of the failure.
        return resendExperienceItem({
          orgId: d.instance.data.orgId,
          experienceId: d.instance.id,
          itemIds: [originalTask.id],
          itemType: 'TASK',
          actor: taskTpl.actor,
          channels: taskTpl.channels.filter(
            (c) => c.type === ScheduleChannelType.EMAIL
          )
        }).then(() =>
          // SC-33594: clear the ASSIGNEE_BOUNCED instanceError backing this row
          // once the resend succeeds; otherwise the "…bounced" issue persists.
          clearAssigneeBounceIssuesForItem({
            instanceId: d.instance.id,
            itemType: d.itemType,
            itemTplId: d.itemTplId,
            email: d.email
          })
        );
      }
      return Promise.reject(
        new Error(
          'Cannot retry: original task context not found on this instance. Try resending from the timeline view.'
        )
      );
    }

    // No retry path exists for the remaining types, so reject rather than
    // report the retry as queued (SC-36146):
    // - CC_SEND_ERROR: the only resend endpoint (`schedules_resendExperienceItem`)
    //   sends to the actor's own channels and never re-sends CC copies. The
    //   pieces to build one exist in main-v2 `schedules/src/ccRecipients.ts`
    //   (`sendCCForMessage`/`sendCCForTasks`, which re-resolve recipients from
    //   current data) but no callable wires them up.
    // - CANNOT_COMPLETE_LMS_MODULE: the Docebo enrol op lives in the
    //   external-operations worker; its terminal errors (no actor, no email,
    //   not a Docebo user, unit not found) abort the op for good and no
    //   callable re-queues it. A retry would mirror
    //   `checkr_retryBackgroundCheckInvitation`.
    // The new issues UI hides Retry for both.
    return Promise.reject(
      new Error(
        'Retry is not available for this issue. Ignore it or resend from the timeline view.'
      )
    );
  };

  const onIgnoreHandler = (d: ExperienceProblemData): Promise<any> => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_INSTANCE,
      'Instance ID': d.instance.id,
      name: 'Issue Ignored',
      Explanation:
        'Admin user clicks ignore button in issues list modal for just one issue',
      'Issue Details':
        getIssueDetailsText(d, org.customUserFields, t, {
          stripFormattingFragments: true
        }) || '',
      // `d` is `D` (= ExperienceProblemData & { participant: Doc<IUser> }) at
      // runtime — the table threads participant through every row — but the
      // public callback signature is the narrower ExperienceProblemData.
      'Timeline Item Title': getIssueTitleText(
        d,
        t,
        (d as { participant?: Doc<IUser> }).participant
      ),
      'Timeline Item Type': getIssueTypeForTracking(d),
      'Timeline Item ID': getIssueItemIdsForTracking(d)
    });
    return dispatchIgnore(d);
  };

  /** Ignores `ds` — all from ONE instance — with one write per ignore helper
   * (each helper is one transaction on the experience doc), awaited in turn so
   * they never contend with each other. Types with no ignore path are skipped. */
  const ignoreIssuesOfInstance = async (
    instanceId: string,
    ds: ExperienceProblemData[]
  ) => {
    const actions = ds.filter(isAutomationProblem);
    const sentItems = ds.filter(
      (d): d is SentItemErrorData => d.type === 'SENT_ITEM'
    );
    const missingData = ds.filter(
      (d) => d.type === 'MISSING_ACTOR' || d.type === 'MISSING_VAR'
    );
    const supportingActors = ds.filter(
      (d) => d.type === ErrorDataType.CANNOT_ASSIGN_SUPPORTING_ACTOR
    );
    const docusign = ds.filter(
      (d) => d.type === ErrorDataType.CANNOT_SEND_DOCUSIGN_ENVELOPE
    );
    const instanceErrors = ds.filter((d) =>
      INSTANCE_ERROR_ISSUE_TYPES.has(d.type)
    );

    if (actions.length) {
      await skipActionsAndUpdate(
        instanceId,
        Object.entries(groupBy(actions, (d) => d.automationTpl.id)).map(
          ([automationTplId, xs]) => ({
            automationTplId,
            actionTplIds: xs.map((x) => x.actionTpl.id)
          })
        ),
        currentUserId,
        now()
      );
    }
    if (sentItems.length) {
      // Every failed attempt each row stands for, not just its newest one —
      // otherwise the next-newest attempt resurfaces as the issue.
      await ignoreSentItemIssues(
        instanceId,
        flatten(sentItems.map(getIssueAttemptMsgIds))
      );
    }
    if (missingData.length) {
      await ignoreMissingDataIssue(instanceId, missingData, currentUserId);
    }
    if (supportingActors.length) {
      await ignoreSupportingActorIssue({
        instanceId,
        issues: supportingActors,
        currentUserId
      });
    }
    if (docusign.length) {
      await ignoreDocusignIssue({ instanceId, issues: docusign, currentUserId });
    }
    if (instanceErrors.length) {
      await ignoreInstanceErrorIssues({ instanceId, issues: instanceErrors });
    }
  };

  /** The ignore call for one issue, with no tracking — same split as
   * `dispatchRetry`. */
  const dispatchIgnore = (d: ExperienceProblemData): Promise<void> =>
    ignoreIssuesOfInstance(d.instance.id, [d]);

  const handleIgnoreSelected = () => ignoreManyWithFeedback(selectedItems);

  const retryWithFeedback = async (d: ExperienceProblemData) => {
    try {
      await onRetryHandler(d);
    } catch {
      enqueueSnackbarError(t('Could not retry this item'));
      return;
    }
    markRetriesQueued([d]);
    enqueueSnackbarSuccess(t(RETRY_QUEUED_COPY));
  };

  const handleRetrySelected = () => retryManyWithFeedback(selectedItems);

  const trackIssueListOpened = () => {
    if (data) {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        'Experience Template ID': tpl.id,
        name: 'Issue List Opened',
        Explanation: 'Admin user opens the issues list modal',
        'Number Of Issues': data.length
      });
    }
  };

  const trackIssueListClosed = () => {
    if (data) {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        'Experience Template ID': tpl.id,
        name: 'Issue List Closed',
        Explanation: 'Admin user closes the issues list modal',
        'Number Of Issues': data.length
      });
    }
  };

  const onOpenDialog = () => {
    trackIssueListOpened();
    openDialog();
  };

  const onCloseDialog = () => {
    trackIssueListClosed();
    closeDialog();
  };

  const openNewIssuesModal = () => {
    trackIssueListOpened();
    setNewIssuesModalOpen(true);
  };

  const closeNewIssuesModal = () => {
    trackIssueListClosed();
    setNewIssuesModalOpen(null);
  };

  const logBulkRejections = (
    action: 'retry' | 'ignore',
    results: PromiseSettledResult<unknown>[]
  ) => {
    results.forEach((r) => {
      if (r.status === 'rejected') {
        console.error(`Bulk ${action} failed for one issue`, r.reason);
      }
    });
  };

  const isAutomationProblem = (
    d: ExperienceProblemData
  ): d is AutomationActionErrorData => d.type === 'AUTOMATION';

  // Bulk retry for both issues UIs (legacy footer + new modal). One "Multiple
  // Issues Retried" event per click, then the same per-type dispatch a single
  // click uses, so every family behaves identically in bulk. SC-36146: rows
  // whose retry is already queued are skipped, accepted retries are marked
  // queued, and the outcome is surfaced as a snackbar. Automations go as one
  // callable — the backend batches by action — and every other type is one
  // callable per issue, so those are rate-limited.
  const retryManyWithFeedback = async (ds: ExperienceProblemData[]) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      'Experience Template ID': tpl.id,
      name: 'Multiple Issues Retried',
      Explanation: 'Admin user retries multiple issues in issues list modal',
      'Number Of Issues': ds.length,
      'Number Of Instances': uniq(ds.map((x) => x.instance.id)).length
    });
    const retryable = ds.filter(isRetryableIssue);
    const toRetry = retryable.filter((d) => getRetryCooldownMs(d) === 0);
    const skipped = retryable.length - toRetry.length;
    const automations = toRetry.filter(isAutomationProblem);
    const others = toRetry.filter((d) => !isAutomationProblem(d));

    const [automationResults, perItemResults] = await Promise.all([
      automations.length
        ? Promise.allSettled([
            forceRunAutomationActions(
              tpl.data.orgId,
              automations.map((d) => ({
                experienceTplId: d.tpl.id,
                automationTplId: d.automationTpl.id,
                actionTplId: d.actionTpl.id,
                instanceId: d.instance.id
              })),
              Date.now()
            )
          ])
        : Promise.resolve([] as PromiseSettledResult<unknown>[]),
      mapWithConcurrencySettled(others, BULK_RETRY_CONCURRENCY, dispatchRetry)
    ]);
    logBulkRejections('retry', [...automationResults, ...perItemResults]);

    const succeeded = [
      ...(automationResults[0]?.status === 'fulfilled' ? automations : []),
      ...others.filter((_, i) => perItemResults[i].status === 'fulfilled')
    ];
    const failed = toRetry.length - succeeded.length;
    markRetriesQueued(succeeded);

    if (failed) {
      enqueueSnackbarError(
        failed === 1
          ? t('Could not retry this item')
          : t('Could not retry these items')
      );
    } else if (succeeded.length || skipped) {
      // Skipped rows already have a retry in progress.
      enqueueSnackbarSuccess(t(RETRY_QUEUED_COPY));
    }
  };

  const retryQueuedBanner =
    recentlyQueuedRetries > 0 ? (
      <RetryQueuedBanner severity="info">
        {t(
          '{{ count }} retries in progress. Retries can take up to 2 minutes. If unsuccessful, the issue will re-appear here automatically.',
          { count: recentlyQueuedRetries }
        )}
      </RetryQueuedBanner>
    ) : null;

  // Bulk ignore for both issues UIs. One "Multiple Issues Ignored" event per
  // click, then ONE grouped write set per instance (`ignoreIssuesOfInstance`)
  // rather than one transaction per issue — a group is often N rows on the same
  // experience doc (e.g. one participant's bad address), which would otherwise
  // contend and fire the doc's onWrite triggers N times. Instances run with
  // bounded concurrency; a failure is reported, and its rows stay in the list.
  const ignoreManyWithFeedback = async (ds: ExperienceProblemData[]) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      'Experience Template ID': tpl.id,
      name: 'Multiple Issues Ignored',
      Explanation: 'Admin user ignores multiple issues in issues list modal',
      'Number Of Issues': ds.length,
      'Number Of Instances': uniq(ds.map((x) => x.instance.id)).length
    });
    const results = await mapWithConcurrencySettled(
      Object.entries(groupBy(ds, (d) => d.instance.id)),
      ISSUE_ACTION_CONCURRENCY,
      ([instanceId, xs]) => ignoreIssuesOfInstance(instanceId, xs)
    );
    logBulkRejections('ignore', results);
    if (results.some((r) => r.status === 'rejected')) {
      enqueueSnackbarError(
        ds.length === 1
          ? t('Could not ignore this item')
          : t('Could not ignore some of these items')
      );
    }
  };

  return (
    <>
      <Alert
        severity="error"
        action={
          <Button
            variant="outlined"
            color="inherit"
            onClick={issuesBarRedesignEnabled ? openNewIssuesModal : onOpenDialog}
          >
            {t('Investigate...')}
          </Button>
        }
      >
        {title}
      </Alert>
      {issuesBarRedesignEnabled && newIssuesModalOpen && (
        <IssuesModal
          open={newIssuesModalOpen}
          onClose={closeNewIssuesModal}
          tpl={tpl}
          instances={instances}
          org={org}
          ctx={{
            t,
            customUserFields: org.customUserFields,
            userFieldLabel: (key) =>
              isCustomFieldKey(key)
                ? org.customUserFields.find((f) => toCustomFieldKey(f.key) === key)?.label
                : standardUserFields(t).find((f) => f.key === key)?.label,
            getAnchorFields,
            routes: ROUTES,
            onOpenParticipant: (instance) => setParticipantDialogInstanceId(instance.id),
            onOpenTimelineItem: (ref) => setTimelineItemId(toTimelineItemIdParam(ref))
          }}
          onRetry={retryWithFeedback}
          onIgnore={onIgnoreHandler}
          onRetryMany={retryManyWithFeedback}
          onIgnoreMany={ignoreManyWithFeedback}
          getRetryCooldownMs={getRetryCooldownMs}
          banner={retryQueuedBanner}
        />
      )}
      {loading && <Loader />}
      {!issuesBarRedesignEnabled && dialogOpen && (
        <Dialog
          open={dialogOpen}
          onClose={onCloseDialog}
          fullWidth
          maxWidth="xl"
          fullScreen
        >
          <DialogActionsHeader
            onClose={onCloseDialog}
            left={
              <Typography component="h2" variant="h4">
                {title}
              </Typography>
            }
          />

          <DialogContent>
            {retryQueuedBanner}
            <InstanceProblemsTable
              tpl={tpl}
              ds={data}
              selected={selected}
              setSelected={setSelected}
              onRetry={retryWithFeedback}
              onIgnore={(d) => onIgnoreHandler(d)}
              getRetryCooldownMs={getRetryCooldownMs}
            />
          </DialogContent>
          <DialogActionsWithSlots
            left={
              <>
                {!!selectedItems.length && (
                  <Typography variant="body1">
                    {selectedItems.length === 1
                      ? t('1 item selected')
                      : t('{{ count }} items selected', {
                          count: selectedItems.length
                        })}
                  </Typography>
                )}
              </>
            }
            right={
              <>
                {!!selectedItems.length && (
                  <>
                    <ButtonWithPromise
                      variant="contained"
                      color="primary"
                      pending={t('Ignore selected')}
                      onClick={handleIgnoreSelected}
                    >
                      {t('Ignore selected')}
                    </ButtonWithPromise>
                    <ButtonWithPromise
                      variant="contained"
                      color="primary"
                      onClick={handleRetrySelected}
                      pending={t('Retry selected')}
                    >
                      {t('Retry selected')}
                    </ButtonWithPromise>
                  </>
                )}
                <Button onClick={onCloseDialog}>{t('Close')}</Button>
              </>
            }
          />
        </Dialog>
      )}
    </>
  );
};

const BarItem = styled('div')((p) => ({
  width: '100%'
}));

const ContentExperienceDetailsInner = ({
  tpl
}: {
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useTranslation();
  const { t: ut } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const { startTimer, getDuration } = useTrackDuration();
  const { org } = currentUser;
  const anchorFields = useAnchorFields(org.customUserFields);
  const [undoData, setUndoData] = useState<IExperienceTemplate | null>(null);
  const { ROUTES } = useRoutes();
  const { trackEvent } = useProductAnalytics();
  const tplId = tpl.id;

  const [d] = useEnrollmentFormByTemplateOrGetEmptyDefault(tpl, currentUser.id);
  const ref = useRef<{ scrollToStartingPosition: () => void }>(null);

  useEffect(() => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Admin View Experience Page Loaded',
      'Experience Template ID': tplId,
      Explanation: 'Admin user loads the read-only Experience page'
    });
  }, [trackEvent, tplId]);

  console.log({ tpl });

  const canEdit = hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, [
    'edit'
  ]);

  const [selectedItem, setSelectedItem] = useTaskTimelineItemSelection(tpl);
  const onGoToOtherItem = useCallback(
    (otherItem: ITimelineItem) => {
      if (otherItem.itemType === 'AUTOMATION') {
        setSelectedItem({ type: 'AUTOMATION', item: otherItem.item });
      }
      if (otherItem.itemType === 'MSG') {
        setSelectedItem({ type: 'MSG', item: otherItem.item });
      }
      if (otherItem.itemType === 'TASK') {
        setSelectedItem({ type: 'TASK', item: otherItem.item });
      }
    },
    [setSelectedItem]
  );

  const [
    isManualEnrollmentDialogOpen,
    setManualEnrollmentDialogOpen
  ] = useBooleanQueryParam('manualEnrollmentOpen', false);
  const [
    isEnrollmentFormDialogOpen,
    setEnrollmentFormDialogOpen
  ] = useBooleanQueryParam('enrollmentFormOpen', false);

  const [
    enrollmentFormDialogTab,
    setEnrollmentFormDialogTab
  ] = useEnrollmentDialogTabQueryParam();

  return (
    <>
      <PageBody noTopPadding>
        <TeaserBoxFreeTierForExperiences>
          {ut(
            'This is a preview of Experiences, which enables you to run personalized, automated HR processes at scale.'
          )}
        </TeaserBoxFreeTierForExperiences>
        <PageToolbar sticky>
          <PageToolbarTitle>
            <Typography component="h1" variant="h1" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{tpl.data.adminTitle || tpl.data.title}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <TemplateStatusChip status={tpl.data.status} size="small" />
                {tpl.data.aiFeatureUsed && <CopilotBadge size="small" />}
              </span>
            </Typography>
          </PageToolbarTitle>
          <PageToolbarActions>
            <AILifecycleAnalysis templateId={tpl.id} />
            <ExperiencePreviewV2Button
              d={tpl}
              startIcon={<Eye {...ICON_STYLE} />}
            >
              {t('Preview')}
            </ExperiencePreviewV2Button>
            {!isMobile && (
              <>
                {canEdit && (
                  <Link
                    to={
                      tpl.data.status === ExperienceTemplateStatus.ACTIVE
                        ? ROUTES.experiences.details.edit.url(tpl.id, {
                            step: 'tasks'
                          })
                        : ROUTES.experiences.details.edit.url(tpl.id)
                    }
                  >
                    <Button
                      {...BUTTON_STYLE}
                      startIcon={<Edit {...ICON_STYLE} />}
                      onClick={() => {
                        trackEvent({
                          eventType: PAEventType.EXPERIENCE_TPL,
                          name: 'Edit Experience Button Clicked',
                          'Experience Template ID': tplId,
                          Explanation:
                            'Admin user clicks the edit button on the read-only Experience page'
                        });
                      }}
                    >
                      {t('Edit')}
                    </Button>
                  </Link>
                )}
                {canEdit && (
                  <EnrollmentMenu
                    disabled={
                      tpl.data.status !== ExperienceTemplateStatus.ACTIVE
                    }
                    onEnrollmentFormClick={() =>
                      setEnrollmentFormDialogOpen(true)
                    }
                    onManualEnrollClick={() =>
                      setManualEnrollmentDialogOpen(true)
                    }
                  />
                )}
                <MoreActions tpl={tpl} onSetUndoData={setUndoData} />
              </>
            )}
          </PageToolbarActions>
        </PageToolbar>

        <Section>
          <TemplateMetaGrid
            d={tpl}
            resourceTooltipMode="inProgress"
            showTitle={!!tpl.data.adminTitle}
          />
        </Section>

        {!HIDE_OLD_AUTOMATION_PROBLEMS && (
          <SectionAutomationProblems tpl={tpl} />
        )}

        <Section>
          <FlexContainer
            direction="column"
            spacing={1}
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            {tpl.data.status === ExperienceTemplateStatus.ACTIVE &&
              d &&
              d.data.status === EnrollmentFormStatus.ACTIVE && (
                <>
                  <BarItem>
                    <EnrollmentFormUrlBanner
                      d={d}
                      onViewSettings={() => setEnrollmentFormDialogOpen(true)}
                    />
                  </BarItem>
                  <BarItem>
                    <EnrollmentFormSubmissionsBanner
                      d={d}
                      onViewSubmissions={() => {
                        setEnrollmentFormDialogTab('submissions');
                        setEnrollmentFormDialogOpen(true);
                      }}
                    />
                  </BarItem>
                </>
              )}
              {undoData && (
                <BarItem>
                  <Alert
                    severity="info"
                    action={
                      <Button 
                        variant="outlined" 
                        color="inherit" 
                        onClick={() => {
                          updateDoc(tpl, () => undoData);
                          setUndoData(null);
                        }}
                      >
                        Undo CSV Import
                      </Button>
                    }
                    onClose={() => setUndoData(null)}
                  >
                    CSV import applied. You can undo this change.
                  </Alert>
                </BarItem>
              )}
            <BarItem>
              <IssueBar tpl={tpl} />
            </BarItem>
          </FlexContainer>
        </Section>

        <Section>
          <SectionTitle hasSubHeadline>
            <FlexContainer justifyContent="space-between">
              <Typography component="h2" variant="h2">
                {t('Timeline')}
              </Typography>

              <TemplateSearchButton
                tpl={tpl}
                variant="contained"
                color="primary"
                onSelectMatch={setSelectedItem}
              />
            </FlexContainer>
          </SectionTitle>
          <TaskTimeline
            tpl={tpl}
            hasZoomControls
            anchorFields={anchorFields}
            onSelectItem={setSelectedItem}
          />
          {selectedItem &&
            (selectedItem.type === 'MSG' ? (
              <MessageTemplateDialog
                open={true}
                onClose={() => setSelectedItem(null)}
                value={selectedItem.item}
                expTpl={tpl}
                mode="read"
                startTimer={startTimer}
                getDuration={getDuration}
                onGoToOtherItem={onGoToOtherItem}
              />
            ) : selectedItem.type === 'TASK' ? (
              <TaskTemplateReadOnlyDialog
                open={true}
                onClose={() => setSelectedItem(null)}
                tpl={selectedItem.item}
                anchorFields={anchorFields}
                expTpl={tpl}
                onGoToOtherItem={onGoToOtherItem}
              />
            ) : selectedItem.type === 'AUTOMATION' ? (
              <AutomationTemplateReadOnlyDialog
                open={true}
                onClose={() => setSelectedItem(null)}
                tpl={selectedItem.item}
                expTpl={tpl}
                anchorFields={anchorFields}
                customFields={org.customUserFields}
                resources={tpl.data.resources}
                onGoToOtherItem={onGoToOtherItem}
              />
            ) : null)}
        </Section>
        <Section>
          <SectionTitle>
            <Typography
              component="h2"
              variant="h2"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                ref.current?.scrollToStartingPosition();
              }}
            >
              {t('Schedule')}
            </Typography>
          </SectionTitle>
          <CalendarV2 tpl={tpl} ref={ref} />
        </Section>

        <Section>
          <SectionTitle hasSubHeadline>
            <Typography component="h2" variant="h2">
              {t('Participants')}
            </Typography>
          </SectionTitle>
          <Participants tpl={tpl} customFields={org.customUserFields} />
        </Section>
      </PageBody>

      {tpl.data.status === ExperienceTemplateStatus.ACTIVE &&
        isManualEnrollmentDialogOpen && (
          <ManualEnrollModal
            open={isManualEnrollmentDialogOpen}
            onClose={() => setManualEnrollmentDialogOpen(null)}
            tpl={tpl}
          />
        )}
      {isEnrollmentFormDialogOpen && (
        <EnrollmentFormDialog
          tpl={tpl}
          isTemplateInactive={
            tpl.data.status !== ExperienceTemplateStatus.ACTIVE
          }
          open={true}
          onClose={() => {
            setEnrollmentFormDialogOpen(null);
            setEnrollmentFormDialogTab(null);
          }}
          tab={enrollmentFormDialogTab}
          onChangeTab={setEnrollmentFormDialogTab}
        />
      )}
    </>
  );
};

const ContentExperienceDetails = ({ tplId }: { tplId: string }) => {
  const [tpl, loading, error] = useExperienceTemplateById(tplId);
  useErrorLogger('tpl by id', error);
  if (loading) {
    return <Loader height={500} />;
  }

  if (!tpl) {
    return null;
  }
  return <ContentExperienceDetailsInner tpl={tpl} />;
};

export const PageExperienceDetails = ({ tplId }: { tplId: string }) => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();
  const { t } = useUnsafeTranslation();

  if (!hasCurrentUserExperienceLevelScopes(currentUser, tplId, ['view'])) {
    return (
      <NoAccess
        title={t('You can’t view this experience')}
        action={t('viewing this experience')}
        buttonLabel={t('Go to Experiences List')}
        buttonLink={ROUTES.experiences.overview.url()}
      />
    );
  }
  return <ContentExperienceDetails tplId={tplId} />;
};
